import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import JsBarcode from 'jsbarcode';
import SecurityMonitor from '../components/dashboard/SecurityMonitor';
import UsersView from '../components/dashboard/UsersView';
import FleetView from '../components/dashboard/FleetView';
import TrainingView from '../components/dashboard/TrainingView';
import MessagesView from '../components/dashboard/MessagesView';
import AdminView from '../components/dashboard/AdminView';
import AnnouncementsFeed from '../components/dashboard/AnnouncementsFeed';
import TxtConvContent from '../components/dashboard/TxtConvContent';
import VoiceAssistant from '../components/dashboard/VoiceAssistant';
import InstallPrompt from '../components/InstallPrompt';

// Extracted Widgets
import {
  DailyFeedWidget,
  CalendarWidget,
  RideroOrdersWidget,
  RideroDriversWidget,
  RideroDailyFeedWidget,
  TasksWidget,
  TrucksWidget,
  StatsWidget,
  DriversWidget,
  SafetyWidget,
  ComplianceWidget,
  BadgesWidget,
  StreakWidget
} from '../components/dashboard/widgets';

// Extracted Hub Components
import {
  NAV_ITEMS,
  APPS,
  AVAILABLE_WIDGETS,
  getFilteredApps,
  DEFAULT_WIDGETS
} from '../components/dashboard/hub/constants/hubConstants';

import {
  DesktopPointsContent,
  DesktopApprovalsContent,
  DesktopKudosContent,
  DesktopTeamsContent,
  DesktopCalendarContent,
  DesktopDocumentsContent,
  DesktopAppsContent,
  DesktopSettingsContent
} from '../components/dashboard/hub/desktop';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Home, Mail, MessageSquareLock, LayoutDashboard, UserCheck,
  ShieldCheck, Users, Briefcase, Shield, Coins, Sliders,
  BarChart3, Link2, Activity, Settings, LogOut, Palette,
  Truck, Wrench, ClipboardCheck, User, Barcode, Gift,
  ExternalLink, MapPin, Zap, FileCheck, ClipboardList, Menu, X,
  ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, Star, Copy, GripVertical, Plus, Minus, Share2, Save, Check, Calculator,
  Video, RefreshCw, Clock, Bell, Heart, MessageCircle, Send, Upload, Eye, Trash2, Building, Edit,
  Award, UsersRound, Calendar, FileText, FolderOpen, ThumbsUp, Search, Lock, AlertTriangle,
  Download, History, CheckCircle, Mic
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sortable Widget Component
function SortableWidget({ id, children, isEditMode, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !isEditMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      {isEditMode && (
        <>
          <div {...attributes} {...listeners}
            className="absolute -top-2 left-1/2 -translate-x-1/2 z-10 w-8 h-8 bg-accent rounded-full flex items-center justify-center cursor-grab active:cursor-grabbing shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
            <GripVertical className="w-4 h-4 text-accent-foreground" />
          </div>
          <button onClick={() => onRemove(id)}
            className="absolute -top-2 -right-2 z-10 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600">
            <Minus className="w-4 h-4 text-white" />
          </button>
        </>
      )}
      {children}
    </div>
  );
}

// Widget registry - uses imported widget components
const WIDGET_COMPONENTS = {
  tasks: TasksWidget,
  trucks: TrucksWidget,
  stats: StatsWidget,
  drivers: DriversWidget,
  safety: SafetyWidget,
  compliance: ComplianceWidget,
  badges: BadgesWidget,
  streak: StreakWidget,
  ridero_orders: RideroOrdersWidget,
  ridero_drivers: RideroDriversWidget,
  ridero_daily_feed: RideroDailyFeedWidget,
};

// Mobile Home Content - Shows info cards, apps, and widgets
function MobileHomeContent({ user, messageCount, navigate, setShowBarcode, copyReferralCode, handleAppLaunch, getFilteredApps, activeWidgets, isEditMode, setIsEditMode, showAddWidget, setShowAddWidget, availableToAdd, addWidget, removeWidget, sensors, handleDragStart, handleDragEnd, activeId, isSaving, saveBoard, shareBoard, boardId }) {
  return (
    <>
      {/* Info Cards */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-gradient-to-br from-yellow-500/25 to-yellow-600/10 rounded-2xl p-4 border border-yellow-500/30 active:scale-95 transition-transform" onClick={() => navigate('/points')}>
          <div className="w-10 h-10 rounded-xl bg-yellow-500/30 flex items-center justify-center mb-2"><Coins className="w-6 h-6 text-yellow-400" /></div>
          <p className="text-2xl font-bold text-yellow-400">{user?.points_balance || 0}</p>
          <p className="text-xs text-yellow-400/70 font-medium">Points</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500/25 to-blue-600/10 rounded-2xl p-4 border border-blue-500/30 active:scale-95 transition-transform">
          <div className="w-10 h-10 rounded-xl bg-blue-500/30 flex items-center justify-center mb-2 relative">
            <Mail className="w-6 h-6 text-blue-400" />
            {messageCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white">{messageCount}</span>}
          </div>
          <p className="text-2xl font-bold text-blue-400">{messageCount}</p>
          <p className="text-xs text-blue-400/70 font-medium">Messages</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500/25 to-purple-600/10 rounded-2xl p-4 border border-purple-500/30 active:scale-95 transition-transform" onClick={() => setShowBarcode(true)}>
          <div className="w-10 h-10 rounded-xl bg-purple-500/30 flex items-center justify-center mb-2"><Barcode className="w-6 h-6 text-purple-400" /></div>
          <p className="text-sm font-bold text-purple-400 truncate">{user?.barcode?.slice(0, 6) || 'N/A'}</p>
          <p className="text-xs text-purple-400/70 font-medium">Barcode</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="col-span-2 bg-gradient-to-br from-green-500/25 to-green-600/10 rounded-2xl p-4 border border-green-500/30 active:scale-[0.98] transition-transform" onClick={copyReferralCode}>
          <div className="flex items-start justify-between">
            <div>
              <div className="w-10 h-10 rounded-xl bg-green-500/30 flex items-center justify-center mb-2"><Gift className="w-6 h-6 text-green-400" /></div>
              <p className="text-xs text-green-400/70 font-medium mb-1">Referral Code</p>
              <p className="text-lg font-bold text-green-400 font-mono">{user?.referral_code || 'N/A'}</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center"><Copy className="w-5 h-5 text-green-400" /></div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500/25 to-amber-600/10 rounded-2xl p-4 border border-amber-500/30">
          <div className="w-10 h-10 rounded-xl bg-amber-500/30 flex items-center justify-center mb-2"><Shield className="w-6 h-6 text-amber-400" /></div>
          <p className="text-xs font-bold text-amber-400 capitalize leading-tight">{user?.role_id === 'master_admin' ? 'Admin' : user?.role_id?.replace('_', ' ') || 'User'}</p>
          <p className="text-xs text-amber-400/70 font-medium">Role</p>
        </div>
      </div>

      {/* Connected Apps */}
      <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-4 border border-border/30 mb-4">
        <p className="text-xs text-muted-foreground font-medium mb-3 uppercase tracking-wider">Connected Apps</p>
        <div className="grid grid-cols-3 gap-4">
          {getFilteredApps(user?.role_id, user?.connected_apps).map((app) => {
            const Icon = app.icon;
            return (
              <button key={app.id} onClick={() => handleAppLaunch(app.id)} className="flex flex-col items-center gap-2 active:scale-90 transition-transform">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: `${app.color}25`, boxShadow: `0 4px 20px ${app.color}40` }}>
                  <Icon className="w-7 h-7" style={{ color: app.color }} />
                </div>
                <span className="text-[10px] font-medium text-muted-foreground text-center">{app.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Widgets Section */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <LayoutDashboard className="w-5 h-5 text-accent" />
          Widgets
        </h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAddWidget(!showAddWidget)} className="gap-1">
            <Plus className="w-4 h-4" />
          </Button>
          <Button variant={isEditMode ? "default" : "outline"} size="sm" onClick={() => setIsEditMode(!isEditMode)} className="gap-1">
            <GripVertical className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {showAddWidget && availableToAdd.length > 0 && (
        <div className="bg-card/60 backdrop-blur-xl rounded-2xl p-4 border border-accent/30 mb-4">
          <p className="text-sm font-medium mb-3">Add a widget:</p>
          <div className="flex flex-wrap gap-2">
            {availableToAdd.map((widget) => {
              const Icon = widget.icon;
              return (
                <button key={widget.type} onClick={() => addWidget(widget.type)} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors">
                  <Icon className="w-4 h-4" style={{ color: widget.color }} />
                  <span className="text-sm">{widget.title}</span>
                  <Plus className="w-4 h-4 text-green-500" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <SortableContext items={activeWidgets.map(w => w.id)} strategy={rectSortingStrategy}>
          <div className={`grid grid-cols-1 gap-4 ${isEditMode ? 'ring-2 ring-accent/30 ring-dashed rounded-3xl p-4 bg-accent/5' : ''}`}>
            {activeWidgets.map((widget) => {
              const WidgetComponent = WIDGET_COMPONENTS[widget.type];
              if (!WidgetComponent) return null;
              return (
                <SortableWidget key={widget.id} id={widget.id} isEditMode={isEditMode} onRemove={removeWidget}>
                  <WidgetComponent />
                </SortableWidget>
              );
            })}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeId ? (() => {
            const widget = activeWidgets.find(w => w.id === activeId);
            const WidgetComponent = widget ? WIDGET_COMPONENTS[widget.type] : null;
            return WidgetComponent ? <div className="opacity-80 rotate-3 scale-105"><WidgetComponent /></div> : null;
          })() : null}
        </DragOverlay>
      </DndContext>

      {/* Company Announcements Feed */}
      <div className="mt-6">
        <AnnouncementsFeed isAdmin={user?.role_id === 'master_admin' || user?.role_id === 'admin'} />
      </div>
    </>
  );
}

// Mobile Users Content
function MobileUsersContent() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${API}/users`, { withCredentials: true });
        setUsers(res.data.slice(0, 10));
      } catch (e) {
        console.error('Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  if (loading) return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-accent" /></div>;

  return (
    <div className="space-y-3">
      {users.map((u) => (
        <div key={u.user_id} className="bg-card/60 backdrop-blur-xl rounded-2xl p-4 border border-border/30 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center">
            <User className="w-6 h-6 text-accent" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{u.name}</p>
            <p className="text-xs text-muted-foreground truncate">{u.email}</p>
          </div>
          <div className={`px-2 py-1 rounded-lg text-xs ${u.is_approved ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
            {u.is_approved ? 'Active' : 'Pending'}
          </div>
        </div>
      ))}
    </div>
  );
}

// Mobile Messages Content
function MobileMessagesContent() {
  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-2xl p-6 border border-border/30 text-center">
      <Mail className="w-12 h-12 text-blue-400 mx-auto mb-4" />
      <h3 className="text-lg font-bold mb-2">Messages</h3>
      <p className="text-sm text-muted-foreground">No new messages</p>
    </div>
  );
}

// Mobile Points Content
function MobilePointsContent({ user }) {
  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-yellow-500/25 to-yellow-600/10 rounded-3xl p-6 border border-yellow-500/30 text-center">
        <Coins className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
        <p className="text-4xl font-bold text-yellow-400">{user?.points_balance || 0}</p>
        <p className="text-sm text-yellow-400/70 mt-2">Total Points</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-card/60 rounded-2xl p-4 border border-border/30 text-center">
          <p className="text-2xl font-bold text-green-400">+50</p>
          <p className="text-xs text-muted-foreground">Earned Today</p>
        </div>
        <div className="bg-card/60 rounded-2xl p-4 border border-border/30 text-center">
          <p className="text-2xl font-bold text-blue-400">5</p>
          <p className="text-xs text-muted-foreground">Referrals</p>
        </div>
      </div>
    </div>
  );
}

// Mobile Approvals Content
function MobileApprovalsContent() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPending = async () => {
      try {
        const res = await axios.get(`${API}/users/pending`, { withCredentials: true });
        setPending(res.data);
      } catch (e) {
        console.error('Failed to load pending users');
      } finally {
        setLoading(false);
      }
    };
    fetchPending();
  }, []);

  if (loading) return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-accent" /></div>;

  return (
    <div className="space-y-3">
      {pending.length === 0 ? (
        <div className="bg-card/60 rounded-2xl p-6 border border-border/30 text-center">
          <UserCheck className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <p className="text-muted-foreground">No pending approvals</p>
        </div>
      ) : (
        pending.map((u) => (
          <div key={u.user_id} className="bg-card/60 rounded-2xl p-4 border border-border/30 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
              <User className="w-6 h-6 text-yellow-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium">{u.name}</p>
              <p className="text-xs text-muted-foreground">{u.email}</p>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// Mobile Admin Content
function MobileAdminContent() {
  return (
    <div className="space-y-3">
      <div className="bg-card/60 rounded-2xl p-4 border border-border/30 flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-purple-400" />
        </div>
        <div>
          <p className="font-medium">Admin Controls</p>
          <p className="text-xs text-muted-foreground">Manage system settings</p>
        </div>
      </div>
      <div className="bg-card/60 rounded-2xl p-4 border border-border/30 flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
          <Shield className="w-6 h-6 text-blue-400" />
        </div>
        <div>
          <p className="font-medium">Role Management</p>
          <p className="text-xs text-muted-foreground">Configure user roles</p>
        </div>
      </div>
    </div>
  );
}

// Mobile Fleet Content
function MobileFleetContent() {
  const [stats, setStats] = useState({ trucks: 0, drivers: 0 });
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('drivers');
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [showEditDriver, setShowEditDriver] = useState(null);
  const [driverForm, setDriverForm] = useState({ name: '', email: '', phone: '', license_number: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, driversRes, trucksRes] = await Promise.all([
        axios.get(`${API}/fleet/stats`, { withCredentials: true }),
        axios.get(`${API}/fleet/drivers`, { withCredentials: true }),
        axios.get(`${API}/fleet/trucks`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setDrivers(driversRes.data?.drivers || driversRes.data || []);
      setTrucks(trucksRes.data?.trucks || trucksRes.data || []);
    } catch (e) {
      console.error('Failed to load fleet data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const saveDriver = async () => {
    if (!driverForm.name) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (showEditDriver) {
        await axios.patch(`${API}/fleet/drivers/${showEditDriver.driver_id}`, driverForm, { withCredentials: true });
        toast.success('Driver updated!');
      } else {
        await axios.post(`${API}/fleet/drivers`, driverForm, { withCredentials: true });
        toast.success('Driver added!');
      }
      setShowAddDriver(false);
      setShowEditDriver(null);
      setDriverForm({ name: '', email: '', phone: '', license_number: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save driver');
    } finally {
      setSaving(false);
    }
  };

  const deleteDriver = async (driverId, driverName) => {
    if (!window.confirm(`Delete driver "${driverName}"?`)) return;
    try {
      await axios.delete(`${API}/fleet/drivers/${driverId}`, { withCredentials: true });
      toast.success('Driver deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete driver');
    }
  };

  const openEditDriver = (driver) => {
    setDriverForm({
      name: driver.name || '',
      email: driver.email || '',
      phone: driver.phone || '',
      license_number: driver.license_number || ''
    });
    setShowEditDriver(driver);
  };

  if (loading) return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-accent" /></div>;

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-orange-500/25 to-orange-600/10 rounded-2xl p-4 border border-orange-500/30 text-center">
          <Truck className="w-10 h-10 text-orange-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-orange-400">{stats.trucks?.total || trucks.length || 0}</p>
          <p className="text-xs text-orange-400/70">Trucks</p>
        </div>
        <div className="bg-gradient-to-br from-cyan-500/25 to-cyan-600/10 rounded-2xl p-4 border border-cyan-500/30 text-center">
          <Users className="w-10 h-10 text-cyan-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-cyan-400">{stats.drivers?.total || drivers.length || 0}</p>
          <p className="text-xs text-cyan-400/70">Drivers</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('drivers')}
          className={`flex-1 py-2.5 rounded-xl font-medium text-sm ${activeTab === 'drivers' ? 'bg-accent text-black' : 'bg-secondary'}`}
        >
          Drivers
        </button>
        <button
          onClick={() => setActiveTab('trucks')}
          className={`flex-1 py-2.5 rounded-xl font-medium text-sm ${activeTab === 'trucks' ? 'bg-accent text-black' : 'bg-secondary'}`}
        >
          Trucks
        </button>
      </div>

      {/* Drivers Tab */}
      {activeTab === 'drivers' && (
        <div className="space-y-3">
          <button
            onClick={() => { setShowAddDriver(true); setDriverForm({ name: '', email: '', phone: '', license_number: '' }); }}
            className="w-full py-3 bg-accent text-black rounded-xl font-bold flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" /> Add Driver
          </button>
          
          {drivers.length === 0 ? (
            <div className="bg-card/40 rounded-xl p-6 text-center text-muted-foreground">
              No drivers yet. Add your first driver.
            </div>
          ) : (
            drivers.map((driver) => (
              <div key={driver.driver_id} className="bg-card/60 rounded-xl p-4 border border-border/30">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{driver.name}</p>
                    <p className="text-sm text-muted-foreground truncate">{driver.email || 'No email'}</p>
                    {driver.phone && <p className="text-xs text-muted-foreground">{driver.phone}</p>}
                  </div>
                  <div className="flex gap-1">
                    <button 
                      onClick={() => openEditDriver(driver)}
                      className="p-2 hover:bg-accent/20 rounded-lg text-accent"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => deleteDriver(driver.driver_id, driver.name)}
                      className="p-2 hover:bg-red-500/20 rounded-lg text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Trucks Tab */}
      {activeTab === 'trucks' && (
        <div className="space-y-3">
          {trucks.length === 0 ? (
            <div className="bg-card/40 rounded-xl p-6 text-center text-muted-foreground">
              No trucks yet.
            </div>
          ) : (
            trucks.map((truck) => (
              <div key={truck.truck_id} className="bg-card/60 rounded-xl p-4 border border-border/30">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-orange-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{truck.unit_number || truck.truck_id}</p>
                    <p className="text-sm text-muted-foreground">{truck.make} {truck.model}</p>
                  </div>
                  <div className={`px-2 py-1 rounded-lg text-xs ${truck.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    {truck.status || 'Active'}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Add/Edit Driver Modal */}
      {(showAddDriver || showEditDriver) && (
        <div className="fixed inset-0 bg-black/60 flex items-end justify-center z-50" onClick={() => { setShowAddDriver(false); setShowEditDriver(null); }}>
          <div className="bg-card rounded-t-3xl p-6 w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-4">{showEditDriver ? 'Edit Driver' : 'Add Driver'}</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">Driver Name *</label>
                <input
                  type="text"
                  value={driverForm.name}
                  onChange={e => setDriverForm({...driverForm, name: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Email</label>
                <input
                  type="email"
                  value={driverForm.email}
                  onChange={e => setDriverForm({...driverForm, email: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Phone</label>
                <input
                  type="tel"
                  value={driverForm.phone}
                  onChange={e => setDriverForm({...driverForm, phone: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  placeholder="+1 555-1234"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">License Number</label>
                <input
                  type="text"
                  value={driverForm.license_number}
                  onChange={e => setDriverForm({...driverForm, license_number: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  placeholder="CDL123456"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowAddDriver(false); setShowEditDriver(null); }} className="flex-1 py-3 bg-secondary rounded-xl font-medium">Cancel</button>
              <button 
                onClick={saveDriver} 
                disabled={!driverForm.name || saving}
                className="flex-1 py-3 bg-accent text-black rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Mobile Training Content  
function MobileTrainingContent() {
  const [videos, setVideos] = useState([]);
  const [links, setLinks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('videos');
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoForm, setVideoForm] = useState({ title: '', url: '', duration: '', description: '' });
  const [sendForm, setSendForm] = useState({ driver_id: '', send_email: true });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [videosRes, linksRes, driversRes] = await Promise.all([
          axios.get(`${API}/training/videos`, { withCredentials: true }),
          axios.get(`${API}/training/links`, { withCredentials: true }),
          axios.get(`${API}/fleet/drivers`, { withCredentials: true })
        ]);
        setVideos(Array.isArray(videosRes.data) ? videosRes.data : []);
        setLinks(Array.isArray(linksRes.data) ? linksRes.data : []);
        const driversData = driversRes.data?.drivers || driversRes.data;
        setDrivers(Array.isArray(driversData) ? driversData : []);
      } catch (e) {
        console.error('Failed to load training data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const addVideo = async () => {
    try {
      await axios.post(`${API}/training/videos`, videoForm, { withCredentials: true });
      toast.success('Video added!');
      setShowAddVideo(false);
      setVideoForm({ title: '', url: '', duration: '', description: '' });
      const res = await axios.get(`${API}/training/videos`, { withCredentials: true });
      setVideos(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add video');
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await axios.delete(`${API}/training/videos/${videoId}`, { withCredentials: true });
      toast.success('Video deleted!');
      setVideos(videos.filter(v => v.video_id !== videoId));
    } catch (e) {
      toast.error('Failed to delete video');
    }
  };

  const sendToDriver = async () => {
    if (!selectedVideo || !sendForm.driver_id) return;
    setSending(true);
    try {
      const linkRes = await axios.post(`${API}/training/generate-link`, {
        video_id: selectedVideo.video_id,
        driver_id: sendForm.driver_id
      }, { withCredentials: true });

      if (sendForm.send_email) {
        try {
          await axios.post(`${API}/training/send-email`, {
            link_token: linkRes.data.link_token,
            driver_id: sendForm.driver_id
          }, { withCredentials: true });
          toast.success('Training link sent via email!');
        } catch (emailError) {
          toast.error(emailError.response?.data?.detail || 'Email failed - but link was generated');
        }
      } else {
        toast.success('Training link generated!');
      }

      setShowSendModal(false);
      setSelectedVideo(null);
      setSendForm({ driver_id: '', send_email: true });
      
      const res = await axios.get(`${API}/training/links`, { withCredentials: true });
      setLinks(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send training');
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'watched': return 'bg-green-500/20 text-green-400';
      case 'opened': return 'bg-blue-500/20 text-blue-400';
      case 'sent': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  if (loading) return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-accent" /></div>;

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-gradient-to-br from-red-500/25 to-red-600/10 rounded-xl p-3 border border-red-500/30 text-center">
          <Video className="w-6 h-6 text-red-400 mx-auto mb-1" />
          <p className="text-lg font-bold text-red-400">{videos.length}</p>
          <p className="text-xs text-red-400/70">Videos</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500/25 to-blue-600/10 rounded-xl p-3 border border-blue-500/30 text-center">
          <Send className="w-6 h-6 text-blue-400 mx-auto mb-1" />
          <p className="text-lg font-bold text-blue-400">{links.length}</p>
          <p className="text-xs text-blue-400/70">Sent</p>
        </div>
        <div className="bg-gradient-to-br from-green-500/25 to-green-600/10 rounded-xl p-3 border border-green-500/30 text-center">
          <Check className="w-6 h-6 text-green-400 mx-auto mb-1" />
          <p className="text-lg font-bold text-green-400">{links.filter(l => l.status === 'watched').length}</p>
          <p className="text-xs text-green-400/70">Done</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('videos')}
          className={`flex-1 py-2.5 rounded-xl font-medium text-sm ${activeTab === 'videos' ? 'bg-accent text-black' : 'bg-secondary'}`}
        >
          Videos
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 py-2.5 rounded-xl font-medium text-sm ${activeTab === 'history' ? 'bg-accent text-black' : 'bg-secondary'}`}
        >
          History
        </button>
      </div>

      {/* Videos Tab */}
      {activeTab === 'videos' && (
        <div className="space-y-3">
          <button
            onClick={() => setShowAddVideo(true)}
            className="w-full py-3 bg-accent text-black rounded-xl font-bold flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" /> Add Video
          </button>
          
          {videos.length === 0 ? (
            <div className="bg-card/40 rounded-xl p-6 text-center text-muted-foreground">
              No videos yet. Add your first training video.
            </div>
          ) : (
            videos.map((v) => (
              <div key={v.video_id} className="bg-card/60 rounded-xl p-4 border border-border/30">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                    <Video className="w-5 h-5 text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{v.title}</p>
                    <p className="text-xs text-muted-foreground">{v.duration || 'N/A'}</p>
                  </div>
                  <button 
                    onClick={() => deleteVideo(v.video_id)}
                    className="p-2 hover:bg-red-500/20 rounded-lg text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={() => { setSelectedVideo(v); setShowSendModal(true); }}
                  className="w-full mt-3 py-2.5 bg-accent/20 text-accent rounded-lg font-medium flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" /> Send to Driver
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-3">
          {links.length === 0 ? (
            <div className="bg-card/40 rounded-xl p-6 text-center text-muted-foreground">
              No training sent yet
            </div>
          ) : (
            links.map((link) => (
              <div key={link.link_token} className="bg-card/60 rounded-xl p-4 border border-border/30">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{link.video_title || 'Training Video'}</p>
                    <p className="text-sm text-muted-foreground">{link.driver_name || link.driver_id}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(link.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className={`text-xs px-2.5 py-1 rounded-lg ${getStatusColor(link.status)}`}>
                      {link.status?.toUpperCase() || 'PENDING'}
                    </span>
                  </div>
                </div>
                {/* Progress Bar */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">Watch Progress</span>
                    <span className="font-medium">{link.progress || 0}%</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${link.progress >= 100 ? 'bg-green-500' : link.progress > 0 ? 'bg-blue-500' : 'bg-gray-500'}`}
                      style={{ width: `${link.progress || 0}%` }}
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Add Video Modal */}
      {showAddVideo && (
        <div className="fixed inset-0 bg-black/60 flex items-end justify-center z-50" onClick={() => setShowAddVideo(false)}>
          <div className="bg-card rounded-t-3xl p-6 w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-4">Add Training Video</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">Video Title *</label>
                <input
                  type="text"
                  value={videoForm.title}
                  onChange={e => setVideoForm({...videoForm, title: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  placeholder="Safety Training Video"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Video URL *</label>
                <input
                  type="url"
                  value={videoForm.url}
                  onChange={e => setVideoForm({...videoForm, url: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Duration</label>
                <input
                  type="text"
                  value={videoForm.duration}
                  onChange={e => setVideoForm({...videoForm, duration: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  placeholder="15:30"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Description</label>
                <textarea
                  value={videoForm.description}
                  onChange={e => setVideoForm({...videoForm, description: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                  rows={2}
                  placeholder="What this training covers..."
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowAddVideo(false)} className="flex-1 py-3 bg-secondary rounded-xl font-medium">Cancel</button>
              <button onClick={addVideo} disabled={!videoForm.title || !videoForm.url} className="flex-1 py-3 bg-accent text-black rounded-xl font-bold disabled:opacity-50">Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Send to Driver Modal */}
      {showSendModal && selectedVideo && (
        <div className="fixed inset-0 bg-black/60 flex items-end justify-center z-50" onClick={() => setShowSendModal(false)}>
          <div className="bg-card rounded-t-3xl p-6 w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Send Training</h3>
            <p className="text-muted-foreground mb-4 truncate">"{selectedVideo.title}"</p>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">Select Driver *</label>
                <select
                  value={sendForm.driver_id}
                  onChange={e => setSendForm({...sendForm, driver_id: e.target.value})}
                  className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                >
                  <option value="">Choose a driver...</option>
                  {drivers.map(d => (
                    <option key={d.driver_id} value={d.driver_id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-secondary">
                <div>
                  <p className="font-medium text-sm">Send Email</p>
                  <p className="text-xs text-muted-foreground">Email the link</p>
                </div>
                <button 
                  onClick={() => setSendForm({...sendForm, send_email: !sendForm.send_email})}
                  className={`w-12 h-6 rounded-full transition-colors ${sendForm.send_email ? 'bg-green-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transform transition-transform ${sendForm.send_email ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowSendModal(false); setSelectedVideo(null); }} className="flex-1 py-3 bg-secondary rounded-xl font-medium">Cancel</button>
              <button 
                onClick={sendToDriver} 
                disabled={!sendForm.driver_id || sending}
                className="flex-1 py-3 bg-accent text-black rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Mobile Apps Content
function MobileAppsContent({ handleAppLaunch, getFilteredApps, user }) {
  const apps = getFilteredApps(user?.role_id, user?.connected_apps);
  return (
    <div className="space-y-3">
      {apps.map((app) => {
        const Icon = app.icon;
        return (
          <button 
            key={app.id} 
            onClick={() => handleAppLaunch(app.id)}
            className="w-full bg-card/60 backdrop-blur-xl rounded-2xl p-4 border border-border/30 flex items-center gap-4 active:scale-[0.98] transition-transform"
          >
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${app.color}25` }}>
              <Icon className="w-7 h-7" style={{ color: app.color }} />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium">{app.name}</p>
              <p className="text-xs text-muted-foreground">Launch app</p>
            </div>
            <ExternalLink className="w-5 h-5 text-muted-foreground" />
          </button>
        );
      })}
    </div>
  );
}

// Mobile Settings Content
function MobileSettingsContent({ theme, setTheme, themes }) {
  return (
    <div className="space-y-4">
      <div className="bg-card/60 rounded-2xl p-4 border border-border/30">
        <p className="font-medium mb-3 flex items-center gap-2">
          <Palette className="w-5 h-5 text-accent" />
          Theme
        </p>
        <div className="grid grid-cols-3 gap-2">
          {Object.values(themes).map((t) => (
            <button
              key={t.id}
              onClick={() => setTheme(t.id)}
              className={`p-3 rounded-xl border transition-all ${theme === t.id ? 'border-accent bg-accent/10' : 'border-border/30 bg-card/30'}`}
            >
              <p className="text-xs font-medium">{t.name}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// =====================
// DESKTOP CONTENT COMPONENTS
// =====================

// Announcements Feed Component
function NotificationsBell() {
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=10`, { withCredentials: true });
      setNotifications(res.data);
      setUnreadCount(res.data.filter(n => !n.is_read).length);
    } catch (e) {
      console.error('Failed to load notifications');
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/count`, { withCredentials: true });
      setUnreadCount(res.data.unread_count);
    } catch (e) {}
  };

  const markAsRead = async (notifId) => {
    try {
      await axios.post(`${API}/notifications/${notifId}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => n.notification_id === notifId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) {}
  };

  const getIcon = (type) => {
    switch (type) {
      case 'message': return <Mail className="w-4 h-4 text-blue-400" />;
      case 'points': return <Coins className="w-4 h-4 text-yellow-400" />;
      case 'training': return <Video className="w-4 h-4 text-red-400" />;
      case 'announcement': return <MessageCircle className="w-4 h-4 text-purple-400" />;
      default: return <Bell className="w-4 h-4 text-accent" />;
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => { setShowDropdown(!showDropdown); if (!showDropdown) fetchNotifications(); }}
        className="relative w-10 h-10 rounded-xl bg-card/60 border border-border/30 flex items-center justify-center hover:bg-accent/10 transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />
          <div className="absolute right-0 top-12 w-80 bg-card border border-border rounded-2xl shadow-2xl z-50 overflow-hidden">
            <div className="p-3 border-b border-border/30 flex justify-between items-center">
              <span className="font-bold">Notifications</span>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-accent hover:underline">Mark all read</button>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No notifications</p>
              ) : (
                notifications.map(n => (
                  <button
                    key={n.notification_id}
                    onClick={() => markAsRead(n.notification_id)}
                    className={`w-full p-3 text-left hover:bg-accent/5 border-b border-border/20 ${!n.is_read ? 'bg-accent/5' : ''}`}
                  >
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                        {getIcon(n.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!n.is_read ? 'font-semibold' : ''}`}>{n.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{n.content}</p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function HubDashboard() {
  const { user, logout } = useAuth();
  const { theme, setTheme, themes } = useTheme();
  const navigate = useNavigate();
  
  const [isOnline, setIsOnline] = useState(true);
  const [showThemes, setShowThemes] = useState(false);
  const [showBarcode, setShowBarcode] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [showAddWidget, setShowAddWidget] = useState(false);
  const [boardId, setBoardId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Slide-in sidebar state - first click shows name, second click navigates
  const [hoveredSidebarItem, setHoveredSidebarItem] = useState(null);
  const [pendingNavItem, setPendingNavItem] = useState(null);
  
  // Logo interaction states
  const [showAppsDropdown, setShowAppsDropdown] = useState(false);
  const [dropdownClosing, setDropdownClosing] = useState(false);
  const [logoGlowing, setLogoGlowing] = useState(false);
  const [lastTapTime, setLastTapTime] = useState(0);
  
  // Interactive orbit states
  const [orbitRotation, setOrbitRotation] = useState(0);
  const [isDraggingOrbit, setIsDraggingOrbit] = useState(false);
  const [isOrbitHovered, setIsOrbitHovered] = useState(false);
  const [dragStartAngle, setDragStartAngle] = useState(0);
  const [hoveredApp, setHoveredApp] = useState(null);
  const orbitRef = useRef(null);
  
  // Active widgets
  const [activeWidgets, setActiveWidgets] = useState([
    { id: 'w1', type: 'ridero_orders' },
    { id: 'w2', type: 'ridero_drivers' },
    { id: 'w9', type: 'ridero_daily_feed' },
    { id: 'w3', type: 'tasks' },
    { id: 'w4', type: 'trucks' },
    { id: 'w5', type: 'stats' },
    { id: 'w6', type: 'drivers' },
    { id: 'w7', type: 'badges' },
    { id: 'w8', type: 'streak' },
  ]);
  
  // Mobile tab state - social media style navigation
  const [activeTab, setActiveTab] = useState('home');
  
  const isMasterAdmin = user?.role_id === 'master_admin';
  const isAdmin = isMasterAdmin || user?.role_id === 'admin';

  const filteredNavItems = NAV_ITEMS.filter(item => {
    if (!item.permission) return true;
    if (item.permission === 'master') return isMasterAdmin;
    if (item.permission === 'admin') return isAdmin;
    return false;
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Ensure page is scrolled to top on initial load
  useEffect(() => {
    window.scrollTo(0, 0);
    // Force a small delay to ensure layout is complete
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Load board from API
  useEffect(() => {
    const loadBoard = async () => {
      try {
        const res = await axios.get(`${API}/boards/my`, { withCredentials: true });
        if (res.data && res.data.widgets && res.data.widgets.length > 0) {
          setActiveWidgets(res.data.widgets);
          setBoardId(res.data.board_id);
        }
      } catch (e) {
        console.log('Using default board');
      }
    };
    if (user) loadBoard();
  }, [user]);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const res = await axios.get(`${API}/messages/inbox`, { withCredentials: true });
        setMessageCount(res.data.filter(m => !m.read).length);
      } catch (e) {}
    };
    fetchMessages();
  }, []);

  useEffect(() => {
    if (showBarcode && user?.barcode) {
      setTimeout(() => {
        const canvas = document.getElementById('barcode-canvas');
        if (canvas) {
          JsBarcode(canvas, user.barcode, { format: 'CODE128', width: 2, height: 80, displayValue: true, fontSize: 14, background: 'transparent', lineColor: '#ffffff' });
        }
      }, 100);
    }
  }, [showBarcode, user?.barcode]);

  const toggleOnlineStatus = async () => {
    // Toggle online/offline - logo glow only (no theme switch)
    const newOnline = !isOnline;
    setIsOnline(newOnline);
    
    // Trigger glow animation
    setLogoGlowing(true);
    setTimeout(() => setLogoGlowing(false), 800);
    
    try { await axios.post(`${API}/users/status`, { online: newOnline }, { withCredentials: true }); } catch (e) {}
  };

  // Handle logo tap - single tap vs double tap
  const singleTapTimer = useRef(null);
  
  const handleLogoTap = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300; // ms
    
    if (now - lastTapTime < DOUBLE_TAP_DELAY) {
      // Double tap detected - cancel single tap and toggle dropdown
      if (singleTapTimer.current) {
        clearTimeout(singleTapTimer.current);
        singleTapTimer.current = null;
      }
      toggleDropdown();
      setLastTapTime(0);
    } else {
      // First tap - wait to see if double tap follows
      setLastTapTime(now);
      singleTapTimer.current = setTimeout(() => {
        // Single tap confirmed - toggle online/theme
        toggleOnlineStatus();
        singleTapTimer.current = null;
      }, DOUBLE_TAP_DELAY);
    }
  };

  // Handle app selection from dropdown
  const handleAppSelect = async (appId) => {
    closeDropdownAnimated();
    setTimeout(() => handleAppLaunch(appId), 200);
  };

  // Close dropdown with animation
  const closeDropdownAnimated = () => {
    setDropdownClosing(true);
    setTimeout(() => {
      setShowAppsDropdown(false);
      setDropdownClosing(false);
    }, 180);
  };

  // Close dropdown when tapping outside
  const handleBackdropClick = () => {
    closeDropdownAnimated();
  };

  // Toggle dropdown with animation
  const toggleDropdown = () => {
    if (showAppsDropdown) {
      closeDropdownAnimated();
    } else {
      setShowAppsDropdown(true);
      setIsOrbitHovered(false); // Reset hover so auto-rotate starts immediately
      setIsDraggingOrbit(false); // Reset dragging state
    }
  };

  // Interactive orbit drag handlers
  const getAngleFromCenter = (clientX, clientY) => {
    if (!orbitRef.current) return 0;
    const rect = orbitRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    return Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI);
  };

  const handleOrbitDragStart = (e) => {
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setIsDraggingOrbit(true);
    setDragStartAngle(getAngleFromCenter(clientX, clientY) - orbitRotation);
  };

  const handleOrbitDragMove = (e) => {
    if (!isDraggingOrbit) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const currentAngle = getAngleFromCenter(clientX, clientY);
    setOrbitRotation(currentAngle - dragStartAngle);
  };

  const handleOrbitDragEnd = () => {
    setIsDraggingOrbit(false);
  };

  // Add global mouse/touch listeners for orbit dragging
  useEffect(() => {
    if (isDraggingOrbit) {
      const handleMove = (e) => handleOrbitDragMove(e);
      const handleEnd = () => handleOrbitDragEnd();
      
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove);
      window.addEventListener('touchend', handleEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDraggingOrbit, dragStartAngle]);

  // Auto-rotate orbit when not dragging (remove hover check for mobile-friendly experience)
  useEffect(() => {
    if (showAppsDropdown && !isDraggingOrbit) {
      const interval = setInterval(() => {
        setOrbitRotation(prev => (prev + 0.5) % 360);
      }, 50); // Smooth rotation ~10 degrees per second
      return () => clearInterval(interval);
    }
  }, [showAppsDropdown, isDraggingOrbit]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const copyReferralCode = () => {
    if (user?.referral_code) { navigator.clipboard.writeText(user.referral_code); toast.success('Referral code copied!'); }
  };

  const handleAppLaunch = async (appId) => {
    const app = APPS.find(a => a.id === appId);
    if (!app?.url) {
      toast.error('App URL not configured');
      return;
    }
    
    try {
      // Fetch SSO token using HttpOnly cookie auth (no localStorage needed)
      const res = await axios.get(`${API}/sso/token?app_id=${appId}`, { 
        withCredentials: true  // Cookie handles authentication
      });
      
      if (res.data.token) {
        const sessionToken = res.data.token;
        // Token stored in state param for SSO - not in localStorage
        const state = res.data.state || '';  // CSRF protection state
        
        // Build the SSO URL with session token and state for CSRF protection
        const params = new URLSearchParams({
          session_token: sessionToken,
          state: state,  // CSRF protection
          force_reauth: '1',
          ts: Date.now().toString()  // Cache buster
        });
        const ssoUrl = app.url.includes('?') 
          ? `${app.url}&${params.toString()}`
          : `${app.url}?${params.toString()}`;
        
        console.log('SSO Launch:', ssoUrl);
        // Open in SAME window (not popup/new tab)
        window.location.href = ssoUrl;
      } else {
        toast.error('Unable to get session token');
      }
    } catch (error) {
      console.error('SSO Launch error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired - please login again');
      } else {
        toast.error('Failed to connect to app');
      }
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (over && active.id !== over.id) {
      setActiveWidgets((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleDragStart = (event) => { setActiveId(event.active.id); };

  const addWidget = (type) => {
    const widget = AVAILABLE_WIDGETS.find(w => w.type === type);
    if (!widget) return;
    const newId = `w${Date.now()}`;
    setActiveWidgets([...activeWidgets, { id: newId, type }]);
    setShowAddWidget(false);
    toast.success(`Added ${widget.title}`);
  };

  const removeWidget = (id) => {
    setActiveWidgets(activeWidgets.filter(w => w.id !== id));
    toast.success('Widget removed');
  };

  const saveBoard = async () => {
    setIsSaving(true);
    try {
      const res = await axios.post(`${API}/boards`, {
        name: 'My Dashboard',
        widgets: activeWidgets,
        is_public: true
      }, { withCredentials: true });
      setBoardId(res.data.board_id);
      toast.success('Board saved!');
    } catch (e) {
      toast.error('Failed to save board');
    }
    setIsSaving(false);
  };

  const shareBoard = () => {
    if (boardId) {
      const url = `${window.location.origin}/board/${boardId}`;
      navigator.clipboard.writeText(url);
      toast.success('Share link copied!');
    } else {
      toast.error('Save board first to get share link');
    }
  };

  // Sidebar component - Tab-based navigation (no page changes)
  const SidebarContent = () => {
    const navItems = [
      { tab: 'home', icon: Home, label: 'Hub', color: '#22c55e' },
      { tab: 'users', icon: Users, label: 'Users', color: '#3b82f6' },
      { tab: 'kudos', icon: Award, label: 'Kudos', color: '#f59e0b' },
      { tab: 'teams', icon: UsersRound, label: 'Teams', color: '#10b981' },
      { tab: 'calendar', icon: Calendar, label: 'Calendar', color: '#8b5cf6' },
      { tab: 'documents', icon: FolderOpen, label: 'Documents', color: '#6366f1' },
      { tab: 'approvals', icon: UserCheck, label: 'Approvals', color: '#10b981' },
      { tab: 'admin', icon: ShieldCheck, label: 'Admin', color: '#6366f1' },
      { tab: 'messages', icon: Mail, label: 'Messages', color: '#8b5cf6' },
      { tab: 'points', icon: Coins, label: 'Points', color: '#f59e0b' },
      { tab: 'fleet', icon: Truck, label: 'Fleet', color: '#f97316' },
      { tab: 'training', icon: Video, label: 'Training', color: '#ef4444', permission: 'admin' },
      { tab: 'txt-conv', icon: FileText, label: 'TXT Conv.', color: '#eab308', permission: 'admin' },
      { tab: 'assistant', icon: Mic, label: 'Assistant', color: '#3b82f6' },
      { tab: 'apps', icon: Link2, label: 'Apps', color: '#ec4899' },
      { tab: 'settings', icon: Settings, label: 'Settings', color: '#64748b' },
    ];
    
    // Filter nav items based on user role
    const filteredNavItems = navItems.filter(item => {
      if (!item.permission) return true;
      if (item.permission === 'admin') return isAdmin;
      if (item.permission === 'master') return isMasterAdmin;
      return false;
    });
    
    return (
      <>
        <div className="p-5 border-b border-border/30">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent/40 to-accent/10 flex items-center justify-center border-2 border-accent/50">
              {user?.picture ? <img src={user.picture} alt={user.name} className="w-full h-full rounded-2xl object-cover" /> : <User className="w-8 h-8 text-accent" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-lg truncate">{user?.name}</p>
              <p className="text-sm text-accent uppercase font-semibold">{user?.role_id === 'master_admin' ? 'Admin' : user?.role_id?.replace('_', ' ')}</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {filteredNavItems.map((item) => {
            const isActive = activeTab === item.tab;
            const Icon = item.icon;
            return (
              <button 
                key={item.tab} 
                onClick={() => setActiveTab(item.tab)}
                className={`w-full flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 ${
                  isActive 
                    ? 'bg-accent/20 text-accent font-semibold' 
                    : 'text-muted-foreground hover:bg-secondary/50'
                }`}
                style={{
                  color: isActive ? item.color : undefined,
                  boxShadow: isActive ? `inset 0 0 20px ${item.color}15` : 'none'
                }}
              >
                <Icon size={26} strokeWidth={1.8} />
                <span className="text-base font-medium">{item.label}</span>
                {item.tab === 'messages' && messageCount > 0 && (
                  <span className="ml-auto bg-accent text-accent-foreground text-xs px-2.5 py-1 rounded-full font-bold">{messageCount}</span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-border/30 space-y-2">
          <div className="relative">
            <Button variant="ghost" className="w-full justify-start gap-4 px-4 py-4 rounded-2xl" onClick={() => setShowThemes(!showThemes)}>
              <Palette size={26} /> <span className="text-base">Theme</span>
            </Button>
            {showThemes && (
              <div className="absolute bottom-full left-0 mb-2 p-4 bg-card border border-border rounded-2xl shadow-2xl z-50">
                <div className="flex gap-3">
                  {Object.values(themes).map((t) => (
                    <button key={t.id} onClick={() => { setTheme(t.id); setShowThemes(false); }}
                      className={`w-12 h-12 rounded-xl border-2 ${theme === t.id ? 'border-accent' : 'border-transparent'}`}
                      style={{ background: t.colors.bg }}>
                      <div className="w-5 h-5 rounded-full mx-auto" style={{ backgroundColor: t.colors.accent }} />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <Button variant="ghost" className="w-full justify-start gap-4 px-4 py-4 rounded-2xl text-red-500 hover:bg-red-500/10" onClick={handleLogout}>
            <LogOut size={26} /> <span className="text-base">Logout</span>
          </Button>
        </div>
      </>
    );
  };

  // Get available widgets that aren't already added
  const availableToAdd = AVAILABLE_WIDGETS.filter(w => !activeWidgets.some(aw => aw.type === w.type));

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Preload critical styles to prevent flash */}
      <style>{`
        .hub-logo-container {
          min-height: 160px;
          display: flex;
          align-items: center;
          justify-content: center;
          /* Safe area for iPhone notch - logo sits just under sensor */
          padding-top: max(env(safe-area-inset-top, 12px), 28px);
        }
        .hub-logo-img {
          max-width: 128px;
          max-height: 128px;
          object-fit: contain;
        }
        @media (min-width: 1024px) {
          .hub-logo-container {
            padding-top: 40px;
            min-height: 140px;
          }
          .hub-logo-img {
            max-width: 96px;
            max-height: 96px;
          }
        }
      `}</style>
      
      {/* === FIXED LOGO AT TOP CENTER (Mobile & Desktop) === */}
      {/* Single tap/click = toggle online/offline (glow/dark) */}
      {/* Double tap/click = open/close apps dropdown */}
      <div 
        className="fixed top-0 left-0 right-0 lg:left-80 z-[70] flex flex-col items-center pb-4 pointer-events-none hub-logo-container"
        style={{ 
          background: 'linear-gradient(to bottom, hsl(var(--background)) 0%, hsl(var(--background)) 70%, transparent 100%)',
          minHeight: '200px'
        }}
      >
        <button
          onClick={handleLogoTap}
          className="pointer-events-auto relative"
          data-testid="logo-toggle"
          style={{
            transition: 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
            transform: showAppsDropdown && !dropdownClosing ? 'scale(1.06)' : 'scale(1)'
          }}
        >
          {/* Glow ring animation - shows ONLY on tap OR when menu is open */}
          {(logoGlowing || (showAppsDropdown && !dropdownClosing)) && (
            <div 
              className={`absolute inset-0 rounded-full ${logoGlowing ? 'animate-ping' : ''}`}
              style={{
                background: isOnline 
                  ? 'radial-gradient(circle, rgba(34,197,94,0.5) 0%, transparent 70%)'
                  : 'radial-gradient(circle, rgba(156,163,175,0.5) 0%, transparent 70%)',
                transform: 'scale(1.6)',
                opacity: showAppsDropdown ? 0.7 : 1,
                animation: showAppsDropdown && !logoGlowing ? 'glowPulse 2.5s ease-in-out infinite' : undefined
              }}
            />
          )}
          <style>{`
            @keyframes glowPulse {
              0%, 100% { opacity: 0.5; transform: scale(1.5); }
              50% { opacity: 0.75; transform: scale(1.65); }
            }
          `}</style>
          <img 
            src="/logo-full.png" 
            alt="Leader Hub" 
            className="hub-logo-img h-32 w-32 lg:h-24 lg:w-24 object-contain"
            style={{
              filter: isOnline 
                ? 'drop-shadow(0 0 25px rgba(34,197,94,0.8)) drop-shadow(0 0 50px rgba(34,197,94,0.4))' 
                : 'grayscale(100%) brightness(0.3)',
              transition: 'filter 0.3s ease'
            }}
            loading="eager"
          />
        </button>
        
        {/* Apps Orbit - Logos orbit around center logo */}
        {showAppsDropdown && (
          <>
            {/* Backdrop - tap to close */}
            <div 
              className="fixed inset-0 z-[65] pointer-events-auto" 
              onClick={handleBackdropClick}
              style={{
                animation: dropdownClosing 
                  ? 'backdropFadeOut 0.18s ease-out forwards'
                  : 'backdropFadeIn 0.25s ease-out forwards'
              }}
            />
            {/* Orbit container - positioned lower on screen */}
            <div 
              ref={orbitRef}
              className="absolute pointer-events-auto z-[75]"
              style={{
                width: '350px',
                height: '350px',
                top: '65%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                animation: dropdownClosing 
                  ? 'orbitFadeOut 0.2s ease-out forwards'
                  : 'orbitFadeIn 0.3s ease-out forwards',
                cursor: isDraggingOrbit ? 'grabbing' : 'grab'
              }}
              onMouseDown={handleOrbitDragStart}
              onTouchStart={(e) => { handleOrbitDragStart(e); }}
              onMouseEnter={() => {
                // Small delay before pausing, so it doesn't pause immediately on open
                setTimeout(() => {
                  if (showAppsDropdown) setIsOrbitHovered(true);
                }, 500);
              }}
              onMouseLeave={() => { setIsOrbitHovered(false); setHoveredApp(null); }}
              onTouchEnd={() => { handleOrbitDragEnd(); }}
            >
              <style>{`
                @keyframes orbitFadeIn {
                  from { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
                  to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                }
                @keyframes orbitFadeOut {
                  from { opacity: 1; transform: translate(-50%, -50%) scale(1); }
                  to { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
                }
                @keyframes backdropFadeIn {
                  from { opacity: 0; }
                  to { opacity: 1; }
                }
                @keyframes backdropFadeOut {
                  from { opacity: 1; }
                  to { opacity: 0; }
                }
                .orbit-ring-interactive {
                  display: none;
                }
                .orbit-logo-interactive {
                  position: absolute;
                  width: 70px;
                  height: 70px;
                  border-radius: 50%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  background: rgba(10, 14, 20, 0.95);
                  border: 2px solid rgba(255, 255, 255, 0.15);
                  cursor: pointer;
                  pointer-events: auto;
                  transition: transform 0.2s, box-shadow 0.3s, border-color 0.3s;
                  overflow: hidden;
                }
                .orbit-logo-interactive:hover {
                  transform: scale(1.2) !important;
                  border-color: rgba(255, 255, 255, 0.4);
                }
                .orbit-logo-interactive:active {
                  transform: scale(0.95) !important;
                }
                .orbit-logo-interactive img {
                  width: 45px;
                  height: 45px;
                  object-fit: contain;
                  border-radius: 50%;
                }
                .app-tooltip {
                  position: absolute;
                  padding: 6px 12px;
                  border-radius: 8px;
                  font-size: 12px;
                  font-weight: 600;
                  white-space: nowrap;
                  pointer-events: none;
                  opacity: 0;
                  transform: translateY(5px);
                  transition: opacity 0.2s, transform 0.2s;
                  z-index: 100;
                }
                .app-tooltip.visible {
                  opacity: 1;
                  transform: translateY(0);
                }
                .drag-hint {
                  position: absolute;
                  bottom: -30px;
                  left: 50%;
                  transform: translateX(-50%);
                  font-size: 11px;
                  color: rgba(255,255,255,0.4);
                  white-space: nowrap;
                }
              `}</style>
              
              {/* Faint orbit ring */}
              <div 
                className="orbit-ring-interactive" 
                style={{ transform: `rotate(${orbitRotation}deg)` }} 
              />
              
              {/* Drag hint */}
              <div className="drag-hint">
                {isDraggingOrbit ? '↻ Drag to position...' : '↻ Tap app to launch'}
              </div>
              
              {/* Orbiting app logos */}
              <div 
                className="absolute inset-0"
                style={{ transform: `rotate(${orbitRotation}deg)`, transition: isDraggingOrbit ? 'none' : 'transform 0.1s ease-out' }}
              >
                {getFilteredApps(user?.role_id, user?.connected_apps).map((app, index) => {
                  const filteredApps = getFilteredApps(user?.role_id, user?.connected_apps);
                  const angleStep = 360 / filteredApps.length;
                  const angle = (index * angleStep - 90) * (Math.PI / 180);
                  const radius = 138;
                  const centerX = 175;
                  const centerY = 175;
                  const x = centerX + radius * Math.cos(angle) - 35;
                  const y = centerY + radius * Math.sin(angle) - 35;
                  
                  const logoPath = app.id === 'ridero' ? '/logos/ridero.jpg' 
                    : app.id === 'mechanic_kiosk' ? '/logos/peoria.jpg'
                    : app.id === 'mcanick_scout' ? '/logos/mcanick.svg'
                    : app.id === 'mr_mathematico' ? '/logos/mr_math.svg'
                    : app.id === 'fleet_share' ? '/logos/fleet_share.svg'
                    : '/logos/pti_pector.png';
                  
                  const isHovered = hoveredApp === app.id;
                  
                  return (
                    <div key={app.id} style={{ position: 'absolute', left: `${x}px`, top: `${y}px` }}>
                      {/* Tooltip */}
                      <div 
                        className={`app-tooltip ${isHovered ? 'visible' : ''}`}
                        style={{
                          backgroundColor: app.color,
                          color: '#fff',
                          top: '-40px',
                          left: '50%',
                          transform: `translateX(-50%) rotate(${-orbitRotation}deg)`,
                          boxShadow: `0 4px 20px ${app.color}80`
                        }}
                      >
                        {app.name}
                      </div>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isDraggingOrbit) handleAppSelect(app.id);
                        }}
                        onMouseEnter={() => setHoveredApp(app.id)}
                        onMouseLeave={() => setHoveredApp(null)}
                        className="orbit-logo-interactive"
                        style={{
                          boxShadow: isHovered 
                            ? `0 0 30px ${app.color}, 0 0 60px ${app.color}80`
                            : `0 0 20px ${app.color}60, 0 0 40px ${app.color}30`,
                          transform: `rotate(${-orbitRotation}deg)`,
                          transition: isDraggingOrbit ? 'none' : 'transform 0.1s ease-out, box-shadow 0.3s'
                        }}
                        data-testid={`orbit-app-${app.id}`}
                      >
                        <img src={logoPath} alt={app.name} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-80 bg-card/50 border-r border-border/30 flex-col fixed left-0 top-0 bottom-0 z-40">
        <SidebarContent />
      </aside>

      {/* Bottom Fixed Mobile Navigation - Always Visible */}
      <nav 
        className="lg:hidden fixed bottom-0 left-0 right-0 z-[90]"
        style={{
          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.12) 0%, rgba(10, 14, 20, 0.85) 30%, rgba(10, 14, 20, 0.85) 70%, rgba(139, 92, 246, 0.12) 100%)',
          backdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
        }}
        data-testid="bottom-mobile-nav"
      >
        <div className="flex items-center justify-around py-3 px-2">
          <button 
            onClick={() => setActiveTab('home')}
            className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all duration-200 ${activeTab === 'home' ? 'text-accent scale-110' : 'text-muted-foreground'}`}
          >
            <Home className="w-5 h-5" />
            <span className="text-[10px]">Hub</span>
          </button>
          <button 
            onClick={() => setActiveTab('fleet')}
            data-testid="mobile-tab-fleet"
            className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all duration-200 ${activeTab === 'fleet' ? 'text-accent scale-110' : 'text-muted-foreground'}`}
          >
            <Truck className="w-5 h-5" />
            <span className="text-[10px]">Fleet</span>
          </button>
          <button 
            onClick={() => setActiveTab('training')}
            data-testid="mobile-tab-training"
            className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all duration-200 ${activeTab === 'training' ? 'text-accent scale-110' : 'text-muted-foreground'}`}
          >
            <Video className="w-5 h-5" />
            <span className="text-[10px]">Training</span>
          </button>
          <button 
            onClick={() => setActiveTab('messages')}
            className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all duration-200 ${activeTab === 'messages' ? 'text-accent scale-110' : 'text-muted-foreground'}`}
          >
            <Mail className="w-5 h-5" />
            <span className="text-[10px]">Messages</span>
          </button>
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all duration-200 ${mobileMenuOpen ? 'text-accent' : 'text-muted-foreground'}`}
          >
            <Menu className="w-5 h-5" />
            <span className="text-[10px]">More</span>
          </button>
        </div>
      </nav>

      {/* Backdrop for sidebar */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-[94] bg-black/40" 
          onClick={() => { setMobileMenuOpen(false); setPendingNavItem(null); }}
        />
      )}
      
      {/* Mobile Slide-in Sidebar - Compact, icon-only with inline labels */}
      <div 
        className={`lg:hidden fixed z-[95] transition-transform duration-300 ease-out ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{
          top: '50%',
          left: '0',
          transform: mobileMenuOpen ? 'translateY(-50%)' : 'translateX(-100%) translateY(-50%)',
          backgroundColor: 'rgba(10, 14, 20, 0.94)',
          backdropFilter: 'blur(20px)',
          borderRadius: '0 20px 20px 0',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderLeft: 'none',
          padding: '16px 8px',
          overflow: 'visible',
        }}
      >
        {/* Logo - same size as header logo */}
        <div className="flex justify-center mb-4">
          <img 
            src="/logo-full.png" 
            alt="Hub" 
            className="w-14 h-14 object-contain"
            style={{ filter: 'drop-shadow(0 0 12px rgba(34,197,94,0.6))' }}
          />
        </div>
        
        {/* Navigation Icons - Compact list */}
        <nav className="flex flex-col items-center gap-2" style={{ overflow: 'visible' }}>
          {[
            { tab: 'home', icon: Home, label: 'Hub', color: '#22c55e' },
            { tab: 'users', icon: Users, label: 'Users', color: '#3b82f6' },
            { tab: 'kudos', icon: Award, label: 'Kudos', color: '#f59e0b' },
            { tab: 'teams', icon: UsersRound, label: 'Teams', color: '#10b981' },
            { tab: 'calendar', icon: Calendar, label: 'Calendar', color: '#8b5cf6' },
            { tab: 'documents', icon: FolderOpen, label: 'Docs', color: '#6366f1' },
            { tab: 'messages', icon: Mail, label: 'Messages', color: '#8b5cf6' },
            { tab: 'points', icon: Coins, label: 'Points', color: '#f59e0b' },
            { tab: 'fleet', icon: Truck, label: 'Fleet', color: '#f97316' },
            { tab: 'training', icon: Video, label: 'Training', color: '#ef4444' },
            { tab: 'assistant', icon: Mic, label: 'Assistant', color: '#3b82f6' },
            { tab: 'approvals', icon: UserCheck, label: 'Approvals', color: '#10b981' },
            { tab: 'admin', icon: ShieldCheck, label: 'Admin', color: '#6366f1' },
            { tab: 'apps', icon: Link2, label: 'Apps', color: '#ec4899' },
            { tab: 'settings', icon: Settings, label: 'Settings', color: '#64748b' },
          ].map((item) => {
            const isActive = activeTab === item.tab;
            const isPending = pendingNavItem === item.tab;
            const Icon = item.icon;
            
            return (
              <div key={item.tab} className="relative flex items-center">
                <button
                  onClick={() => {
                    if (pendingNavItem === item.tab) {
                      setActiveTab(item.tab);
                      setMobileMenuOpen(false);
                      setPendingNavItem(null);
                    } else {
                      setPendingNavItem(item.tab);
                    }
                  }}
                  className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
                    isActive 
                      ? 'bg-accent/20' 
                      : isPending 
                        ? 'bg-white/15 scale-105' 
                        : 'hover:bg-white/5'
                  }`}
                  style={{
                    color: isActive ? item.color : isPending ? item.color : 'rgba(255,255,255,0.5)',
                    boxShadow: isActive 
                      ? `0 0 12px ${item.color}50` 
                      : isPending 
                        ? `0 0 15px ${item.color}60` 
                        : 'none'
                  }}
                >
                  <Icon className="w-5 h-5" />
                </button>
                
                {/* Inline label - appears next to icon, 20% smaller than icon */}
                {isPending && (
                  <div 
                    className="absolute left-12 whitespace-nowrap px-3 py-1.5 rounded-lg font-semibold pointer-events-none"
                    style={{
                      backgroundColor: item.color,
                      color: '#fff',
                      fontSize: '12px', /* 20% smaller than icon (20px -> 16px equivalent visually is ~12px for text) */
                      boxShadow: `0 4px 15px ${item.color}70`,
                      animation: 'labelPop 0.15s ease-out'
                    }}
                  >
                    {item.label}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        
        <style>{`
          @keyframes labelPop {
            from { opacity: 0; transform: translateX(-5px); }
            to { opacity: 1; transform: translateX(0); }
          }
        `}</style>
      </div>
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      {/* Main Content - Add top padding for fixed logo and bottom padding for mobile nav */}
      <main className="lg:ml-80 min-h-screen p-4 lg:p-8 pt-40 lg:pt-36 pb-24 lg:pb-8">
        
        {/* Profile Card - Always visible on both mobile and desktop */}
        <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 mb-4">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-accent/40 to-accent/10 flex items-center justify-center border-2 border-accent/50 overflow-hidden">
                {user?.picture ? <img src={user.picture} alt={user.name} className="w-full h-full object-cover" /> : <User className="w-10 h-10 text-accent" />}
              </div>
              <div className="absolute -top-1 -right-1 w-7 h-7 bg-yellow-500 rounded-lg flex items-center justify-center shadow-lg">
                <Star className="w-4 h-4 text-white fill-white" />
              </div>
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold">{user?.name?.split(' ')[0] || 'User'}</h1>
              <p className="text-sm text-muted-foreground">{user?.role_id === 'master_admin' ? 'Admin' : user?.role_id?.replace('_', ' ')}</p>
              <div className="flex items-center gap-2 mt-1 lg:flex">
                <div className={`w-3 h-3 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
                <span className={`text-sm font-medium ${isOnline ? 'text-green-500' : 'text-gray-500'}`}>{isOnline ? 'Online' : 'Offline'}</span>
              </div>
            </div>
            {/* Notifications Bell */}
            <NotificationsBell />
          </div>
        </div>
        
        {/* Fixed Logout Button - Top Right Corner on Mobile */}
        <button 
          onClick={handleLogout} 
          className="lg:hidden fixed top-4 right-4 z-[80] w-14 h-14 rounded-2xl bg-red-500/20 border-2 border-red-500/40 flex items-center justify-center active:scale-95 transition-transform shadow-lg backdrop-blur-xl"
          style={{ marginTop: 'max(env(safe-area-inset-top, 0px), 8px)' }}
          data-testid="mobile-logout-btn"
        >
          <LogOut className="w-7 h-7 text-red-500" />
        </button>

        {/* Mobile Tab Title - Shows which section is active */}
        {activeTab !== 'home' && (
          <div className="lg:hidden mb-4 flex items-center gap-3">
            <button 
              onClick={() => setActiveTab('home')} 
              className="w-10 h-10 rounded-xl bg-card/60 border border-border/30 flex items-center justify-center"
            >
              <Home className="w-5 h-5 text-accent" />
            </button>
            <h2 className="text-xl font-bold capitalize">{activeTab.replace('_', ' ')}</h2>
          </div>
        )}

        {/* MOBILE: Embedded Content based on active tab */}
        <div className="lg:hidden">
          {activeTab === 'home' && (
            <MobileHomeContent 
              user={user}
              messageCount={messageCount}
              navigate={navigate}
              setShowBarcode={setShowBarcode}
              copyReferralCode={copyReferralCode}
              handleAppLaunch={handleAppLaunch}
              getFilteredApps={getFilteredApps}
              APPS={APPS}
              activeWidgets={activeWidgets}
              isEditMode={isEditMode}
              setIsEditMode={setIsEditMode}
              showAddWidget={showAddWidget}
              setShowAddWidget={setShowAddWidget}
              availableToAdd={availableToAdd}
              addWidget={addWidget}
              removeWidget={removeWidget}
              sensors={sensors}
              handleDragStart={handleDragStart}
              handleDragEnd={handleDragEnd}
              activeId={activeId}
              isSaving={isSaving}
              saveBoard={saveBoard}
              shareBoard={shareBoard}
              boardId={boardId}
            />
          )}
          {activeTab === 'users' && <MobileUsersContent />}
          {activeTab === 'messages' && <MobileMessagesContent />}
          {activeTab === 'points' && <MobilePointsContent user={user} />}
          {activeTab === 'approvals' && <MobileApprovalsContent />}
          {activeTab === 'admin' && <MobileAdminContent />}
          {activeTab === 'fleet' && <MobileFleetContent />}
          {activeTab === 'training' && <MobileTrainingContent />}
          {activeTab === 'txt-conv' && <TxtConvContent />}
          {activeTab === 'assistant' && <VoiceAssistant />}
          {activeTab === 'kudos' && <DesktopKudosContent />}
          {activeTab === 'teams' && <DesktopTeamsContent />}
          {activeTab === 'calendar' && <CalendarWidget />}
          {activeTab === 'documents' && <DesktopDocumentsContent />}
          {activeTab === 'apps' && <MobileAppsContent handleAppLaunch={handleAppLaunch} getFilteredApps={getFilteredApps} user={user} />}
          {activeTab === 'settings' && <MobileSettingsContent theme={theme} setTheme={setTheme} themes={themes} />}
        </div>

        {/* DESKTOP: Tab-based content - shows different content based on activeTab */}
        <div className="hidden lg:block">
          
          {/* Tab Title - Shows which section is active (except home) */}
          {activeTab !== 'home' && (
            <div className="mb-6 flex items-center gap-4">
              <button 
                onClick={() => setActiveTab('home')} 
                className="w-12 h-12 rounded-xl bg-card/60 border border-border/30 flex items-center justify-center hover:bg-accent/10 transition-colors"
              >
                <Home className="w-6 h-6 text-accent" />
              </button>
              <h2 className="text-2xl font-bold capitalize">{activeTab.replace('_', ' ')}</h2>
            </div>
          )}

          {/* Desktop Content based on activeTab */}
          {activeTab === 'home' && (
            <>
              {/* Info Cards */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-gradient-to-br from-yellow-500/25 to-yellow-600/10 rounded-2xl p-4 border border-yellow-500/30 hover:scale-[1.02] transition-transform cursor-pointer" onClick={() => setActiveTab('points')}>
                  <div className="w-10 h-10 rounded-xl bg-yellow-500/30 flex items-center justify-center mb-2"><Coins className="w-6 h-6 text-yellow-400" /></div>
                  <p className="text-2xl font-bold text-yellow-400">{user?.points_balance || 0}</p>
                  <p className="text-xs text-yellow-400/70 font-medium">Points</p>
                </div>
                <div className="bg-gradient-to-br from-blue-500/25 to-blue-600/10 rounded-2xl p-4 border border-blue-500/30 hover:scale-[1.02] transition-transform cursor-pointer" onClick={() => setActiveTab('messages')}>
                  <div className="w-10 h-10 rounded-xl bg-blue-500/30 flex items-center justify-center mb-2 relative">
                    <Mail className="w-6 h-6 text-blue-400" />
                    {messageCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white">{messageCount}</span>}
                  </div>
                  <p className="text-2xl font-bold text-blue-400">{messageCount}</p>
                  <p className="text-xs text-blue-400/70 font-medium">Messages</p>
                </div>
                <div className="bg-gradient-to-br from-purple-500/25 to-purple-600/10 rounded-2xl p-4 border border-purple-500/30 hover:scale-[1.02] transition-transform cursor-pointer" onClick={() => setShowBarcode(true)}>
                  <div className="w-10 h-10 rounded-xl bg-purple-500/30 flex items-center justify-center mb-2"><Barcode className="w-6 h-6 text-purple-400" /></div>
                  <p className="text-sm font-bold text-purple-400 truncate">{user?.barcode?.slice(0, 6) || 'N/A'}</p>
                  <p className="text-xs text-purple-400/70 font-medium">Barcode</p>
                </div>
              </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="col-span-2 bg-gradient-to-br from-green-500/25 to-green-600/10 rounded-2xl p-4 border border-green-500/30 active:scale-[0.98] transition-transform" onClick={copyReferralCode}>
            <div className="flex items-start justify-between">
              <div>
                <div className="w-10 h-10 rounded-xl bg-green-500/30 flex items-center justify-center mb-2"><Gift className="w-6 h-6 text-green-400" /></div>
                <p className="text-xs text-green-400/70 font-medium mb-1">Referral Code</p>
                <p className="text-lg font-bold text-green-400 font-mono">{user?.referral_code || 'N/A'}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center"><Copy className="w-5 h-5 text-green-400" /></div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-amber-500/25 to-amber-600/10 rounded-2xl p-4 border border-amber-500/30">
            <div className="w-10 h-10 rounded-xl bg-amber-500/30 flex items-center justify-center mb-2"><Shield className="w-6 h-6 text-amber-400" /></div>
            <p className="text-xs font-bold text-amber-400 capitalize leading-tight">{user?.role_id === 'master_admin' ? 'Admin' : user?.role_id?.replace('_', ' ') || 'User'}</p>
            <p className="text-xs text-amber-400/70 font-medium">Role</p>
          </div>
        </div>

        {/* Connected Apps */}
        <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-4 border border-border/30 mb-4">
          <p className="text-xs text-muted-foreground font-medium mb-3 uppercase tracking-wider">Connected Apps</p>
          <div className="grid grid-cols-3 gap-4">
            {getFilteredApps(user?.role_id, user?.connected_apps).map((app) => {
              const Icon = app.icon;
              return (
                <button key={app.id} onClick={() => handleAppLaunch(app.id)} className="flex flex-col items-center gap-2 active:scale-90 transition-transform">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: `${app.color}25`, boxShadow: `0 4px 20px ${app.color}40` }}>
                    <Icon className="w-7 h-7" style={{ color: app.color }} />
                  </div>
                  <span className="text-[10px] font-medium text-muted-foreground text-center">{app.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Widgets Header with Controls */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <LayoutDashboard className="w-5 h-5 text-accent" />
            Widgets
          </h2>
          <div className="flex items-center gap-2">
            {/* Add Widget Button */}
            <Button variant="outline" size="sm" onClick={() => setShowAddWidget(!showAddWidget)} className="gap-1">
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add</span>
            </Button>
            
            {/* Save Button */}
            <Button variant="outline" size="sm" onClick={saveBoard} disabled={isSaving} className="gap-1">
              {isSaving ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              <span className="hidden sm:inline">{isSaving ? 'Saved' : 'Save'}</span>
            </Button>
            
            {/* Share Button */}
            <Button variant="outline" size="sm" onClick={shareBoard} className="gap-1">
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Share</span>
            </Button>
            
            {/* Edit Mode Toggle */}
            <Button variant={isEditMode ? "default" : "outline"} size="sm" onClick={() => setIsEditMode(!isEditMode)} className="gap-1">
              <GripVertical className="w-4 h-4" />
              <span className="hidden sm:inline">{isEditMode ? 'Done' : 'Edit'}</span>
            </Button>
          </div>
        </div>

        {/* Add Widget Panel */}
        {showAddWidget && availableToAdd.length > 0 && (
          <div className="bg-card/60 backdrop-blur-xl rounded-2xl p-4 border border-accent/30 mb-4">
            <p className="text-sm font-medium mb-3">Add a widget:</p>
            <div className="flex flex-wrap gap-2">
              {availableToAdd.map((widget) => {
                const Icon = widget.icon;
                return (
                  <button
                    key={widget.type}
                    onClick={() => addWidget(widget.type)}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors"
                  >
                    <Icon className="w-4 h-4" style={{ color: widget.color }} />
                    <span className="text-sm">{widget.title}</span>
                    <Plus className="w-4 h-4 text-green-500" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {showAddWidget && availableToAdd.length === 0 && (
          <div className="bg-card/60 rounded-2xl p-4 border border-border/30 mb-4 text-center text-muted-foreground">
            All widgets are already added!
          </div>
        )}

        {/* Draggable Widgets */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <SortableContext items={activeWidgets.map(w => w.id)} strategy={rectSortingStrategy}>
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 ${isEditMode ? 'ring-2 ring-accent/30 ring-dashed rounded-3xl p-4 bg-accent/5' : ''}`}>
              {activeWidgets.map((widget) => {
                const WidgetComponent = WIDGET_COMPONENTS[widget.type];
                if (!WidgetComponent) return null;
                return (
                  <SortableWidget key={widget.id} id={widget.id} isEditMode={isEditMode} onRemove={removeWidget}>
                    <WidgetComponent />
                  </SortableWidget>
                );
              })}
            </div>
          </SortableContext>
          
          <DragOverlay>
            {activeId ? (() => {
              const widget = activeWidgets.find(w => w.id === activeId);
              const WidgetComponent = widget ? WIDGET_COMPONENTS[widget.type] : null;
              return WidgetComponent ? <div className="opacity-80 rotate-3 scale-105"><WidgetComponent /></div> : null;
            })() : null}
          </DragOverlay>
        </DndContext>

        {activeWidgets.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <p>No widgets added. Click "Add" to add widgets to your board.</p>
          </div>
        )}

        {isEditMode && (
          <p className="text-center text-sm text-muted-foreground mt-4">
            Drag to rearrange • Click <Minus className="w-3 h-3 inline text-red-500" /> to remove • Click "Save" to keep changes
          </p>
        )}

        {boardId && (
          <p className="text-center text-xs text-muted-foreground mt-4">
            Share URL: {window.location.origin}/board/{boardId}
          </p>
        )}

        {/* Company Announcements Feed */}
        <div className="mt-8">
          <AnnouncementsFeed isAdmin={user?.role_id === 'master_admin' || user?.role_id === 'admin'} />
        </div>
        
        {/* Daily Operations Feed */}
        <div className="mt-6">
          <DailyFeedWidget isAdmin={user?.role_id === 'master_admin' || user?.role_id === 'admin'} />
        </div>
            </>
          )}
          
          {/* Desktop: Other tab content */}
          {activeTab === 'users' && <UsersView />}
          {activeTab === 'messages' && <MessagesView />}
          {activeTab === 'points' && <DesktopPointsContent user={user} />}
          {activeTab === 'approvals' && <DesktopApprovalsContent />}
          {activeTab === 'admin' && <AdminView />}
          {activeTab === 'fleet' && <FleetView />}
          {activeTab === 'training' && <TrainingView />}
          {activeTab === 'txt-conv' && <TxtConvContent />}
          {activeTab === 'assistant' && <VoiceAssistant />}
          {activeTab === 'kudos' && <DesktopKudosContent />}
          {activeTab === 'teams' && <DesktopTeamsContent />}
          {activeTab === 'calendar' && <CalendarWidget />}
          {activeTab === 'documents' && <DesktopDocumentsContent />}
          {activeTab === 'apps' && <DesktopAppsContent handleAppLaunch={handleAppLaunch} getFilteredApps={getFilteredApps} user={user} />}
          {activeTab === 'settings' && <DesktopSettingsContent theme={theme} setTheme={setTheme} themes={themes} user={user} />}
        </div>{/* End of Desktop Content */}
      </main>

      {/* Barcode Modal */}
      {showBarcode && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={() => setShowBarcode(false)}>
          <div className="bg-card p-8 rounded-3xl border border-border max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-6 text-center">Your Barcode</h3>
            <canvas id="barcode-canvas" className="mx-auto"></canvas>
            <p className="text-center text-sm text-muted-foreground mt-6">Scan at any connected app</p>
          </div>
        </div>
      )}

      {/* PWA Install Prompt */}
      <InstallPrompt />
    </div>
  );
}
